import React from 'react';
import type { WebsiteResponse, UpdateWebsiteRequest } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import type { StatusPillVariant } from '@/shared/components/ui/StatusPill';
import { useWebsiteImportExport } from './useWebsiteImportExport';

interface WebsiteCardProps {
  website: WebsiteResponse;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onImport: (id: string, data: UpdateWebsiteRequest) => void;
  onPublish: (id: string) => void;
  onUnpublish: (id: string) => void;
}

const STATUS: Record<string, { label: string; variant: StatusPillVariant }> = {
  draft: { label: 'Rascunho', variant: 'neutral' },
  published: { label: 'Publicado', variant: 'success' },
  archived: { label: 'Arquivado', variant: 'warning' },
};

/**
 * Card de website (view "Cards"). Sem checkbox — seleção em massa vive só na
 * view de lista/tabela (F34). StatusPill shared, thumb clicável (abre a
 * gestão), botão primário "Editar" + menu ••• com todas as ações secundárias.
 */
export const WebsiteCard: React.FC<WebsiteCardProps> = ({
  website,
  onEdit,
  onDelete,
  onImport,
  onPublish,
  onUnpublish,
}) => {
  const { fileInputRef, handleExport, handleImportClick, handleFileChange } =
    useWebsiteImportExport(website, onImport);

  const status = STATUS[website.status] ?? STATUS.draft;
  const isPublished = website.status === 'published';
  const primaryDomain = website.domains?.[0];

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => onEdit(website.id) },
    isPublished
      ? { label: 'Despublicar', icon: 'eye-off', onClick: () => onUnpublish(website.id) }
      : { label: 'Publicar', icon: 'check-circle', onClick: () => onPublish(website.id) },
    { label: 'Exportar', icon: 'download', onClick: handleExport },
    { label: 'Importar', icon: 'upload', onClick: handleImportClick },
    { label: 'Excluir', icon: 'trash', onClick: () => onDelete(website.id), variant: 'danger' },
  ];

  return (
    <div
      className="group relative rounded-xl border overflow-hidden flex flex-col transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`website-card-${website.id}`}
    >
      {/* Thumb — clicável abre a gestão */}
      <button
        type="button"
        onClick={() => onEdit(website.id)}
        className="relative h-36 flex items-center justify-center w-full"
        style={{ backgroundColor: 'var(--theme-page-background)', borderBottom: '1px solid var(--theme-card-border)' }}
        title="Abrir gestão do site"
        data-testid={`website-card-thumb-${website.id}`}
      >
        <span className="flex flex-col items-center gap-1.5" style={{ color: 'var(--theme-muted-text)' }}>
          <Icon name="globe" className="w-8 h-8 opacity-40" />
          <span className="text-[11px] font-mono tracking-tight truncate max-w-[85%]">
            {primaryDomain ?? 'Sem domínio vinculado'}
          </span>
        </span>

        {/* Status (StatusPill shared — F62) */}
        <StatusPill
          variant={status.variant}
          label={status.label}
          className="absolute top-2.5 right-2.5"
          data-testid={`website-card-status-${website.id}`}
        />
      </button>

      {/* Body */}
      <div className="p-3.5 flex-1 flex flex-col gap-2">
        <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
          {website.name}
        </h3>
        <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          <Icon name="grid" className="w-3.5 h-3.5 flex-none" style={{ opacity: 0.7 }} />
          <span className="truncate">Modelo: {website.modelId}</span>
        </div>

        {/* Domains */}
        <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
          {website.domains && website.domains.length > 0 ? (
            website.domains.map((domain) => (
              <span
                key={domain}
                className="inline-flex items-center gap-1 max-w-full text-[11px] px-2 py-0.5 rounded"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}
              >
                <Icon name="external-link" className="w-3 h-3 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
                <span className="truncate">{domain}</span>
              </span>
            ))
          ) : (
            <span className="text-[11px] italic" style={{ color: 'var(--theme-muted-text)' }}>
              Sem domínio vinculado
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-1.5 px-3 py-2.5" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
        <button
          type="button"
          onClick={() => onEdit(website.id)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-lg text-sm font-semibold transition-colors"
          style={{ border: '1px solid var(--theme-input-border)', color: 'var(--theme-card-text)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          data-testid={`website-card-edit-${website.id}`}
        >
          <Icon name="edit" className="w-3.5 h-3.5" />
          Editar
        </button>
        <CardActionsMenu actions={actions} size="sm" data-testid={`website-card-menu-${website.id}`} />
      </div>

      {/* Input escondido para importar */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        onChange={(e) => { void handleFileChange(e); }}
        className="hidden"
        data-testid={`website-card-import-input-${website.id}`}
      />
    </div>
  );
};

WebsiteCard.displayName = 'WebsiteCard';
